/**
 * FileUploadField Component - File upload with preview
 */

import React, { useCallback, useState } from 'react';
import { Upload, X, File, Image, FileText } from 'lucide-react';
import { FileUploadFieldProps } from '../../../types/forms';
import { BaseField } from './BaseField';

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  name,
  label,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  value,
  accept,
  multiple = false,
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 10,
  preview = true,
  onChange,
  onUpload,
}) => {
  const fieldId = `field-${name}`;
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const files = value ? (Array.isArray(value) ? value : [value]) : [];

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="w-5 h-5" />;
    if (file.type.includes('pdf')) return <FileText className="w-5 h-5 text-red-600" />;
    return <File className="w-5 h-5" />;
  };

  const validateFile = (file: File): string | null => {
    if (maxSize && file.size > maxSize) {
      return `File size exceeds ${formatFileSize(maxSize)}`;
    }

    if (accept) {
      const acceptedTypes = accept.split(',').map(t => t.trim());
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isAccepted = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type.toLowerCase();
        }
        if (type.endsWith('/*')) {
          return file.type.startsWith(type.replace('/*', ''));
        }
        return file.type === type;
      });

      if (!isAccepted) {
        return `File type not accepted. Allowed: ${accept}`;
      }
    }

    return null;
  };

  const handleFileSelect = useCallback(
    async (selectedFiles: FileList | null) => {
      if (!selectedFiles || disabled || readonly) return;

      const fileArray = Array.from(selectedFiles);
      
      // Validate file count
      if (!multiple && fileArray.length > 1) {
        alert('Only one file allowed');
        return;
      }

      if (multiple && files.length + fileArray.length > maxFiles) {
        alert(`Maximum ${maxFiles} files allowed`);
        return;
      }

      // Validate each file
      const validFiles: File[] = [];
      const errors: string[] = [];

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      }

      if (errors.length > 0) {
        alert(errors.join('\n'));
      }

      if (validFiles.length > 0) {
        const newFiles = multiple ? [...files, ...validFiles] : validFiles;
        onChange?.(multiple ? newFiles : newFiles[0]);

        // Handle upload if provided
        if (onUpload) {
          setUploading(true);
          try {
            await onUpload(validFiles);
          } catch (err) {
            console.error('Upload failed:', err);
          } finally {
            setUploading(false);
          }
        }
      }
    },
    [disabled, readonly, multiple, files, maxFiles, onChange, onUpload]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      onChange?.(multiple ? newFiles : null);
    },
    [files, multiple, onChange]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled && !readonly) {
        setIsDragging(true);
      }
    },
    [disabled, readonly]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const dropzoneClasses = `
    relative
    border-2 border-dashed rounded-lg
    p-6 text-center
    transition-colors duration-200
    ${
      isDragging
        ? 'border-blue-400 bg-blue-50'
        : error
        ? 'border-red-300 bg-red-50'
        : 'border-gray-300 hover:border-gray-400'
    }
    ${disabled || readonly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
  `;

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <div>
        {/* Dropzone */}
        <div
          className={dropzoneClasses}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && !readonly && document.getElementById(fieldId)?.click()}
        >
          <input
            id={fieldId}
            name={name}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={disabled || readonly}
            className="sr-only"
            onChange={(e) => handleFileSelect(e.target.files)}
          />

          {uploading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-2" />
              <p className="text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                {isDragging
                  ? 'Drop files here'
                  : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {accept && `Accepted: ${accept}`}
                {maxSize && ` • Max size: ${formatFileSize(maxSize)}`}
              </p>
            </div>
          )}
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getFileIcon(file)}
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>

                {!disabled && !readonly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            ))}

            {/* Image preview */}
            {preview && files.some(f => f.type.startsWith('image/')) && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {files
                  .filter(f => f.type.startsWith('image/'))
                  .map((file, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </BaseField>
  );
};

export default FileUploadField;